import { useUserContext } from "@/context/UserContext";
import "./NewComment.scss";
import { handleImageError } from "@/utils/GalleryFunctions";
import { object, string } from "yup";
import { useFormik } from "formik";
import usePhotos from "@/hooks/usePhotos";
import { usePhotoViewContext } from "@/context/PhotoViewContext";
import { useState } from "react";
import { notifySuccess } from "@/utils/NotificationFunctions";
import { readOnlyDateFormat } from "@/utils/DateFormats";
import { suspendedMessage } from "@/utils/DefaultMessages";
import { useGalleryContext } from "@/context/GalleryContext";
import { LogI } from "@/interfaces/Logs";
import { createLog } from "@/services/LogService";

const loginSchema = object({
  comment: string(),
});

interface NewCommentPropsI {
  photoId: string;
  parentCommentId?: string;
}

function NewComment(props: NewCommentPropsI) {
  const { gallery } = useGalleryContext();
  const { currentUser, userToken } = useUserContext();
  const { newCommentInPhoto } = usePhotos();
  const { getComments } = usePhotoViewContext();
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      comment: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { resetForm }) => {
      console.log(values);
      setLoadingSubmit(true);
      if (props.photoId) {
        await newCommentInPhoto(
          {
            comment: values.comment,
            ...(props.parentCommentId && {
              parentCommentId: props.parentCommentId,
            }),
          },
          props.photoId,
        )
          .then(async (res) => {
            if (res.status === "Success") {
              const log: LogI = {
                type: "Comment",
                typeNumber: 4,
                visitStart: Date.now(),
                gallery_name: gallery?.name ?? "",
                text: res.data.comment,
              };
              await createLog(userToken, log);
              getComments();
              resetForm();
              if (currentUser?.admin === 0) {
                notifySuccess(
                  "Uploaded successfully. The content will be visible after the moderation.",
                );
              }
            }
          })
          .finally(() => {
            setLoadingSubmit(false);
          });
      }
    },
  });

  if (!currentUser)
    return <h3 className="red-text-alert">Log in to write a new comment</h3>;

  if (currentUser && currentUser.readOnly)
    return (
      <h3 className="red-text-alert">
        You are in read only until{" "}
        {readOnlyDateFormat(currentUser.readOnlyEndDate)}
      </h3>
    );

  if (currentUser && currentUser.isSuspended)
    return (
      <h3 className="red-text-alert">
        {suspendedMessage(
          currentUser.suspendedEndDate,
          currentUser.suspendedReason,
        )}
      </h3>
    );

  return (
    <form
      className="new-comment-component-main-container"
      onSubmit={formik.handleSubmit}
    >
      <div className="input-container">
        <img
          src={currentUser.avatar}
          onError={handleImageError}
          alt="user profile avatar"
        />
        <input
          className="font-light"
          type="text"
          id="comment"
          name="comment"
          placeholder="Add a comment"
          value={formik.values.comment}
          onChange={formik.handleChange}
        />
      </div>
      <button
        className="btn linear-bg"
        type="submit"
        disabled={formik.values.comment.length === 0 || loadingSubmit}
      >
        Comment
      </button>
    </form>
  );
}

export default NewComment;
